import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../ui/Button';
import { SERVICE_CATEGORIES } from '../../constants/categories';
import { createSeries } from '../../services/seriesService';
import { useToast } from '../../contexts/ToastContext';

const CreateSeriesModal = ({ onClose, onCreated }) => {
  const { showToast } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('general');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const onPickCover = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const t = title.trim();
    if (!t) {
      setError('Escribe un título');
      return;
    }
    setLoading(true);
    try {
      const res = await createSeries({
        title: t,
        description,
        categoryId,
        coverFile
      });
      if (!res.success) {
        setError(res.error || 'No se pudo crear la serie');
        return;
      }
      showToast('Serie creada');
      onCreated?.(res.series);
      onClose?.();
    } catch (err) {
      setError(err.message || 'No se pudo crear la serie');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Crear serie"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Crear serie</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-sm">
              {error}
              {(String(error).toLowerCase().includes('límite') || String(error).toLowerCase().includes('limite')) && (
                <div className="mt-2 text-sm">
                  <Link to="/premium" className="font-semibold underline underline-offset-2">
                    Ver Premium
                  </Link>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Título *
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              maxLength={80}
              placeholder="Nombre de la serie"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{title.length}/80</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Descripción
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="De qué trata la serie?"
              maxLength={400}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description.length}/400</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Categoría
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              {SERVICE_CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Portada (opcional)
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Recomendado: proporción 3:4 (por ejemplo 1080×1440). Peso máximo: 5MB. Formatos: JPG/PNG/WebP.
            </p>
            <input type="file" accept="image/*" className="hidden" id="series-cover" onChange={onPickCover} />
            <label
              htmlFor="series-cover"
              className="flex items-center justify-center w-full p-4 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-indigo-500 transition-colors"
            >
              <div className="text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">Seleccionar portada</p>
              </div>
            </label>
            {coverPreview && (
              <div className="mt-3">
                <img src={coverPreview} alt="Portada preview" className="w-full max-h-64 object-cover rounded-xl border border-gray-200 dark:border-slate-700" />
              </div>
            )}
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 dark:border-slate-700 flex gap-3">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" variant="gradient" className="flex-1" loading={loading} onClick={handleSubmit}>
            Crear
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateSeriesModal;

